// Employee/components/View/ViewHeader.tsx
import React from "react";
import {
    Box,
    FormControlLabel,
    IconButton,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme as useMuiTheme,
} from "@mui/material";
import Button from "Core/components/elements/Button/Button";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import {styles} from "../../styles/header";
import {addDays, addMonths, addWeeks, endOfWeek, format, startOfMonth, startOfWeek, subWeeks,} from "date-fns";
import {ru} from "date-fns/locale";
import {useTheme as useCustomTheme} from "../../../Theme/ThemeContext";
import EditIcon from '@mui/icons-material/Edit';
import {useNavigate} from 'react-router-dom';

// Helper function to calculate the UTC offset for a specific timezone
const getTimeZoneOffset = (timeZone: string) => {
    try {
        const now = new Date();
        const utcDate = new Date(now.toLocaleString("en-US", {timeZone: "UTC"}));
        const tzDate = new Date(now.toLocaleString("en-US", {timeZone}));

        // Difference in minutes between the UTC time and the timezone's local time
        const offsetInMinutes =
            (tzDate.getTime() - utcDate.getTime()) / (1000 * 60);
        const offsetHours = Math.floor(offsetInMinutes / 60);
        const offsetMinutes = Math.abs(offsetInMinutes % 60);
        const offsetSign = offsetHours >= 0 ? "+" : "-";

        return `${offsetSign}${Math.abs(offsetHours)
            .toString()
            .padStart(2, "0")}:${offsetMinutes.toString().padStart(2, "0")}`;
    } catch (error) {
        return "Unknown";
    }
};

// ViewHeader component
interface ViewHeaderProps {
    currentView: "day" | "week" | "month";
    setView: (view: "day" | "week" | "month") => void;
    currentWeekStart: Date;
    setCurrentWeekStart: (date: Date) => void;
    userType: "client" | "psychologist" | "observer";
    toggleUserType: () => void;
    availableViews: ("day" | "week" | "month")[];
    setAvailableViews: React.Dispatch<
        React.SetStateAction<("day" | "week" | "month")[]>
    >;
    debugging: boolean;
    timeZone: string;
    setTimeZone: (newTimeZone: string) => void;
    showHorizontalLines: boolean;
    setShowHorizontalLines: (show: boolean) => void;
    showVerticalLines: boolean;
    setShowVerticalLines: (show: boolean) => void;
    showLineToggles: boolean;
    showOnlyEvents: boolean;
    setShowOnlyEvents: (show: boolean) => void;
    user?: {
        timezone?: string;
    };
}

export const ViewHeader: React.FC<ViewHeaderProps> = React.memo(
    ({
         currentView,
         setView,
         currentWeekStart,
         setCurrentWeekStart,
         userType,
         toggleUserType,
         availableViews,
         debugging,
         timeZone,
         setTimeZone,
         showHorizontalLines,
         setShowHorizontalLines,
         showVerticalLines,
         setShowVerticalLines,
         showLineToggles,
         showOnlyEvents,
         setShowOnlyEvents,
         user,
     }) => {
        const muiTheme = useMuiTheme();
        const {theme} = useCustomTheme();
        const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
        const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const navigate = useNavigate();

        // Set initial timezone from user preferences
        React.useEffect(() => {
            if (user?.timezone && timeZone !== user.timezone) {
                setTimeZone(user.timezone);
            }
        }, []);

        // Save the selected view to localStorage
        const handleSetView = (view: "day" | "week" | "month") => {
            setView(view);

            if (view === "week") {
                if (currentView === "month") {
                    // Set to the current week's Monday if coming from the month view
                    const currentWeekMonday = startOfWeek(new Date(), {
                        weekStartsOn: 1,
                    });
                    setCurrentWeekStart(currentWeekMonday);
                } else {
                    // Align to the Monday of the week containing the currentWeekStart
                    const currentWeekMonday = startOfWeek(currentWeekStart, {
                        weekStartsOn: 1,
                    });
                    setCurrentWeekStart(currentWeekMonday);
                }
            } else if (view === "day") {
                // Keep the currently selected date for the day view
                setCurrentWeekStart(currentWeekStart);
            } else if (view === "month") {
                // Align to the first day of the month containing the currently selected date
                const currentMonthStart = startOfMonth(currentWeekStart);
                setCurrentWeekStart(currentMonthStart);
            }

            // Save the selected view to localStorage
            localStorage.setItem("selectedView", view);
        };
        // Handle navigation between dates
        const handlePrevious = () => {
            switch (currentView) {
                case "day":
                    const prevDay = addDays(currentWeekStart, -1);
                    setCurrentWeekStart(prevDay);
                    break;
                case "week":
                    const prevWeekMonday = startOfWeek(subWeeks(currentWeekStart, 1), {
                        weekStartsOn: 1,
                    });
                    setCurrentWeekStart(prevWeekMonday);
                    break;
                case "month":
                    const prevMonthStart = startOfMonth(addMonths(currentWeekStart, -1));
                    setCurrentWeekStart(prevMonthStart);
                    break;
            }
        };

        const handleNext = () => {
            switch (currentView) {
                case "day":
                    const nextDay = addDays(currentWeekStart, 1);
                    setCurrentWeekStart(nextDay);
                    break;
                case "week":
                    const nextWeekMonday = startOfWeek(addWeeks(currentWeekStart, 1), {
                        weekStartsOn: 1,
                    });
                    setCurrentWeekStart(nextWeekMonday);
                    break;
                case "month":
                    const nextMonthStart = startOfMonth(addMonths(currentWeekStart, 1));
                    setCurrentWeekStart(nextMonthStart);
                    break;
            }
        };
        // Get date range for display
        const getDateRange = () => {
            switch (currentView) {
                case "day":
                    return format(currentWeekStart, "dd.MM.yyyy");
                case "week":
                    const weekEnd = endOfWeek(currentWeekStart, {weekStartsOn: 1});
                    return `${format(currentWeekStart, "dd.MM.yyyy")} - ${format(
                        weekEnd,
                        "dd.MM.yyyy"
                    )}`;
                case "month":
                    return format(currentWeekStart, "MMMM yyyy", {locale: ru}).replace(
                        /^./,
                        (str) => str.toUpperCase()
                    );
                default:
                    return "";
            }
        };

        return (
            <div className={"fcb py-1 py-sm-2 z-4 top-0 "}>
                <div
                    className={
                        "fr align-items-center justify-content-center px-2 " +
                        "top-0 flex-wrap justify-content-sm-between"
                    }
                    style={{position: "sticky"}}
                >
                    {availableViews.map((view) => (
                        <Button
                            key={view}
                            variant={currentView === view ? "contained" : "outlined"}
                            sx={styles.viewButton}
                            onClick={() => handleSetView(view)}
                        >
                            {isMobile
                                ? view === "month"
                                    ? "М"
                                    : view === "week"
                                        ? "Н"
                                        : "Д"
                                : view === "month"
                                    ? "Месяц"
                                    : view === "week"
                                        ? "Неделя"
                                        : "День"}
                        </Button>
                    ))}
                </div>
                <Box
                    sx={{...styles.navButtons}}
                    className={"frbc p-1 maxh-100px bottom-0 mt-2 gap-2"}
                >
                    <IconButton
                        onClick={handlePrevious}
                        sx={{padding: "8px", border: "none !important"}}
                    >
                        <ArrowBackIosNewIcon className={"fs-5"}/>
                    </IconButton>
                    <Typography variant={isMobile ? "body1" : "h5"} align="center">
                        {getDateRange()}
                    </Typography>
                    <IconButton
                        onClick={handleNext}
                        sx={{padding: "8px", border: "none !important"}}
                    >
                        <ArrowForwardIosIcon className={"fs-5"}/>
                    </IconButton>
                </Box>
                <div className={"frcc gap-1 flex-wrap"}>
                    <div className={"frcc gap-2"}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.text.primary80,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            {`${timeZone} (UTC${getTimeZoneOffset(timeZone)})`}
                            <IconButton
                                size={'small'}
                                onClick={() => navigate('/profile')}
                                sx={{ml: 1}}
                            >
                                <EditIcon fontSize="small"/>
                            </IconButton>
                        </Typography>
                    </div>
                    {showLineToggles && (
                        <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                            <IconButton
                                onClick={() => setShowHorizontalLines(!showHorizontalLines)}
                                size={'small'}
                            >
                                {showHorizontalLines ? <ToggleOnIcon/> : <ToggleOffIcon/>}
                            </IconButton>
                            <Typography variant="body2" sx={{fontSize: "0.875rem"}}>
                                Горизонтальные линии
                            </Typography>
                            <IconButton
                                onClick={() => setShowVerticalLines(!showVerticalLines)}
                                size={'small'}
                            >
                                {showVerticalLines ? <ToggleOnIcon/> : <ToggleOffIcon/>}
                            </IconButton>
                            <Typography variant="body2" sx={{fontSize: "0.875rem"}}>
                                Вертикальные линии
                            </Typography>
                        </Box>
                    )}
                    {debugging && (
                        <>
                            <div className={"frcc gap-1 z-2 flex-wrap"}>
                                <ToggleButtonGroup
                                    value={userType}
                                    exclusive
                                    onChange={toggleUserType}
                                    aria-label="user type"
                                    className={"frcc gap-1 z-2 flex-wrap"}
                                    size={isMobile ? "small" : "medium"}
                                >
                                    <ToggleButton value="client" aria-label="client">
                                        Клиент
                                    </ToggleButton>
                                    <ToggleButton value="psychologist" aria-label="psychologist">
                                        Психолог
                                    </ToggleButton>
                                    <ToggleButton value="observer" aria-label="observer">
                                        Наблюдатель
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </div>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showOnlyEvents}
                                        onChange={(e) => setShowOnlyEvents(e.target.checked)}
                                    />
                                }
                                label="Показать только события"
                            />
                        </>
                    )}
                </div>
            </div>
        );
    }
);
